import http from '@/api/http'
import { sleep } from '@/utils/helpers'
import * as _ from 'lodash'

// Helper function
const getValue = (entries: any[], index: number, key: string): number => {
  return Number(entries[index][key])
}

export interface InfiniteScrollState {
  currentEntryKey: string
  currentEntryKeys: any[]
  entries: any[]
  entriesIndex: number
  oldEntriesIndex: number
  gridEntries: any[]
  gridEntriesColumnIndex: number
  gridEntriesIndex: number
  oldGridEntriesIndex: number
  tempEntries: any[]
  tempEntriesIndex: number
  tempGridEntries: any[]
  enabledInfinite: boolean
  distance: number
  page: number
  savedPosition: RouterPosition
}

export interface FetchPayload {
  url: string
  params?: any
  isGrid?: boolean
}

export interface InfinitePayload {
  delay?: number
  fetchPayload: FetchPayload
  $state: VueInfiniteLoadingEvent
}

interface VueInfiniteLoadingEvent {
  loaded: Function
  complete: Function
  reset: Function
}

interface RouterPosition {
  x: number
  y: number
}

interface SearchOption {
  key: string
  value: number
  reverse?: boolean
}

const defaultState: InfiniteScrollState = {
  currentEntryKey: '',
  currentEntryKeys: [],
  entries: [],
  entriesIndex: 0,
  oldEntriesIndex: 0,
  gridEntries: [],
  gridEntriesColumnIndex: 0,
  gridEntriesIndex: 0,
  oldGridEntriesIndex: 0,
  tempEntries: [],
  tempEntriesIndex: 0,
  tempGridEntries: [],
  enabledInfinite: false,
  distance: 1000,
  page: 0,
  savedPosition: { x: 0, y: 0 }
}

const state: InfiniteScrollState = Object.assign({}, defaultState)

const mutations = {
  setCurrentEntryKey(state: InfiniteScrollState, key: string) {
    state.currentEntryKey = key
  },
  setCurrentEntryKeys(state: InfiniteScrollState, keys: []) {
    state.currentEntryKeys = keys
  },
  setDistance(state: InfiniteScrollState, distance: number) {
    state.distance = distance
  },
  clearGridEntries(state: InfiniteScrollState) {
    state.gridEntries = []
    state.gridEntriesColumnIndex = 0
    state.gridEntriesIndex = 0
    state.oldGridEntriesIndex = 0
  },
  clearEntries(state: InfiniteScrollState) {
    state.entries = []
    state.entriesIndex = 0
    state.oldEntriesIndex = 0
  },
  setEntry(state: InfiniteScrollState, payload: { entry: any; index: number }) {
    state.entries[payload.index] = { ...payload.entry }
  },
  setGridEntry(
    state: InfiniteScrollState,
    payload: { entry: any; index1: number; index2: number }
  ) {
    state.gridEntries[payload.index1][payload.index2] = payload.entry
  },
  /**
   * Grid で表示する場合は Entries を変形させる
   */
  setGridEntries(state: InfiniteScrollState, entries: {}) {
    const keys = Object.keys(entries)
    if (keys.length === 0) {
      return
    }

    state.oldGridEntriesIndex = state.gridEntriesIndex

    let columnsItem = []
    for (let i = 0; i < keys.length; i++) {
      if (keys[i] === 'is_remainning') {
        continue
      }
      columnsItem.push(entries[keys[i]])
      state.gridEntriesColumnIndex++
      const isOdd = keys.length % 2 !== 0
      if (Object.keys(columnsItem).length === 2 || (i === keys.length - 1 && isOdd)) {
        state.gridEntries[state.gridEntriesIndex] = columnsItem
        state.gridEntriesIndex++
        columnsItem = []
      }
    }
    state.gridEntries = Object.assign([], state.gridEntries)
  },
  setEntries(state: InfiniteScrollState, entries: {}) {
    const keys = Object.keys(entries)
    if (keys.length === 0) {
      return
    }

    state.oldEntriesIndex = state.entriesIndex

    for (let i = 0; i < keys.length; i++) {
      if (keys[i] === 'is_remainning') {
        continue
      }
      state.entries[state.entriesIndex] = entries[keys[i]]
      if (!state.entries[state.entriesIndex]['ekey']) {
        state.entries[state.entriesIndex]['ekey'] = keys[i]
      }
      state.entriesIndex++
    }
    state.entries = Object.assign([], state.entries)
  },
  updateEntries(state: InfiniteScrollState, entries: any[]) {
    state.entries = Object.assign([], entries)
  },
  unshiftEntry(state: InfiniteScrollState, entry: {}) {
    state.entries.unshift(entry)
  },
  updateGridEntries(state: InfiniteScrollState, gridEntries: any[]) {
    state.gridEntries = Object.assign([], gridEntries)
  },
  pushEntry(state: InfiniteScrollState, entry: {}) {
    state.entries.push(entry)
  },
  pushGridEntriy(state: InfiniteScrollState, entry: {}) {
    const lastIndex = state.gridEntries.length - 1
    if (state.gridEntries[lastIndex].length === 2) {
      state.gridEntries[state.gridEntries.length].push(entry)
    } else {
      state.gridEntries[lastIndex].push(entry)
    }
  },
  clearTempEntries(state: InfiniteScrollState) {
    state.tempEntries = []
    state.tempEntriesIndex = 0
  },
  setTempEntries(state: InfiniteScrollState, entries: {}) {
    const keys = Object.keys(entries)
    if (keys.length === 0) {
      return
    }
    for (let i = 0; i < keys.length; i++) {
      if (keys[i] === 'is_remainning') {
        continue
      }
      state.tempEntries[state.tempEntriesIndex] = entries[keys[i]]
      state.tempEntriesIndex++
    }
    state.tempEntries = Object.assign([], state.tempEntries)
  },
  setTempGridEntries(state: InfiniteScrollState, gridEntries: []) {
    state.tempGridEntries = gridEntries
  },
  setEnabledInfinite(state: InfiniteScrollState, enabledInfinite: boolean) {
    state.enabledInfinite = enabledInfinite
  },
  setEntriesIndex(state: InfiniteScrollState, index: number) {
    state.entriesIndex = index
  },
  setPage(state: InfiniteScrollState, page: number) {
    state.page = page
  },
  incrementPage(state: InfiniteScrollState) {
    state.page++
  },
  decrementPage(state: InfiniteScrollState) {
    state.page--
  },
  removeGridEntry(state: InfiniteScrollState, keys: number[]) {
    if (keys.length === 2) {
      state.gridEntries[keys[0]][keys[1]] = {}
    }
    state.gridEntries = Object.assign([], state.gridEntries)
  },
  removeEntry(state: InfiniteScrollState, targetIndex: number) {
    state.entries = state.entries.filter((_v, i) => {
      return i !== targetIndex
    })
  },
  setSavedPosition(state: InfiniteScrollState, position: RouterPosition) {
    state.savedPosition = position
  }
}

let isFetching = false

const actions = {
  /**
   * サーバから entries を fetch して state にセット
   */
  async getEntries({ commit }, payload: FetchPayload): Promise<any> {
    if (isFetching) {
      return
    }
    isFetching = true

    // NOTE: params.p は Vue component からでないとリセットできないので注意
    // ページ番号が設定されていなければリセット
    if (!payload.params.p) {
      // distance が後から値を変更しても更新されないのでここでついでにリセット
      commit('clearEntries')
      commit('clearGridEntries')
      commit('setDistance', defaultState.distance)
      commit('setEnabledInfinite', false)
      commit('setPage', 0)
    }

    try {
      const res = await http.post(payload.url, payload.params)
      if (res.data.error) {
        throw new Error(res.data.error)
      }
      if (!res.data.entries || Object.keys(res.data.entries).length === 0) {
        isFetching = false
        return res.data
      }
      // Object.assign(state.entries, state.entries) で順番を入れ替えたりしても反映されなかったり
      // 一括で追加されて勝手にスクロールしてしまう
      if (payload.isGrid) {
        commit('setGridEntries', res.data.entries)
      } else {
        commit('setEntries', res.data.entries)
      }
      if (res.data.is_remainning || res.data.entries.is_remainning) {
        commit('setEnabledInfinite', true)
      }
      isFetching = false
      return res.data
    } catch (err) {
      console.error(err)
      isFetching = false
    }
  },

  /**
   * 無限スクロール
   */
  async infinite({ commit, dispatch, state }, payload: InfinitePayload) {
    console.log('[infinite]')

    const delay = payload.delay ? payload.delay : 300
    await sleep(delay)

    // ページをインクリメントしてから次ページのデータを取得
    commit('incrementPage')
    payload.fetchPayload.params.p = state.page

    try {
      const data = await dispatch('getEntries', payload.fetchPayload)
      if (payload.$state) {
        payload.$state.loaded()
        console.log('[infinite] $state.loaded()')
      }
      if (!data || !data.entries || data.entries.length === 0) {
        commit('setEnabledInfinite', false)
        if (payload.$state) {
          payload.$state.complete()
          console.log('[infinite] $state.complete()')
        }
        return
      }
    } catch (err) {
      return err
    }
  },

  /**
   * entries の中から binary search で value(数値) が一致するものを見つけその index を返す
   */
  async binarySearch({ state }, { key, value, reverse }: SearchOption): Promise<number | null> {
    if (state.entries.length === 0 || !key || typeof value !== 'number') {
      return -1
    }

    let entries = _.cloneDeep(state.entries)
    if (reverse) {
      entries = entries.reverse()
    }
    let index = -1
    let middle = -1
    let left = 0
    let right = entries.length - 1

    // 範囲超え, 最小値, 最大値と比較
    const min = getValue(entries, left, key)
    const max = getValue(entries, right, key)
    if (value < min || max < value) {
      return -1
    } else if (min === value) {
      index = left
    } else if (max === value) {
      index = right
    } else {
      // binary search
      while (left <= right) {
        middle = Math.floor((left + right) / 2)
        const middleValue = getValue(entries, middle, key)

        if (middleValue === value) {
          index = middle
          break
        } else if (middleValue < value) {
          left = middle + 1
        } else {
          right = middle - 1
        }
      }
    }

    if (reverse) {
      // 反転しているのを戻す
      return index > -1 ? entries.length - index - 1 : index
    }
    return index
  },

  /**
   * entries の中から lenear search で value(数値) が一致するものを見つけその index を返す
   */
  async linearSearch({ state }, { key, value }: SearchOption): Promise<number> {
    if (state.entries.length === 0 || !key || typeof value !== 'number') {
      return -1
    }

    const entries = _.cloneDeep(state.entries)
    let find = -1
    let index = 0
    let start = 0
    let end = state.entries.length - 1

    if (value === getValue(entries, start, key)) {
      return start
    } else if (value === getValue(entries, end, key)) {
      return end
    } else {
      while (index < end) {
        index++
        if (value === getValue(entries, index, key)) {
          find = index
          break
        }
      }
    }
    return find
  }
}

const getters = {
  currentEntryKey: (state: InfiniteScrollState): string => state.currentEntryKey,
  currentEntryKeys: (state: InfiniteScrollState): any[] => state.currentEntryKeys,
  entries: (state: InfiniteScrollState): any[] => state.entries,
  gridEntries: (state: InfiniteScrollState): any[] => state.gridEntries,
  tempEntries: (state: InfiniteScrollState): any[] => state.tempEntries,
  tempGridEntries: (state: InfiniteScrollState): any[] => state.tempGridEntries,
  enabledInfinite: (state: InfiniteScrollState): boolean => state.enabledInfinite,
  entriesIndex: (state: InfiniteScrollState): number => state.entriesIndex,
  oldEntriesIndex: (state: InfiniteScrollState): number => state.oldEntriesIndex,
  oldGridEntriesIndex: (state: InfiniteScrollState): number => state.oldGridEntriesIndex,
  distance: (state: InfiniteScrollState): number => state.distance,
  page: (state: InfiniteScrollState): number => state.page,
  savedPosition: (state: InfiniteScrollState): {} => state.savedPosition
}

const InfiniteScrollStore = {
  defaultState: defaultState,
  state,
  mutations,
  actions,
  getters
}

export default InfiniteScrollStore
